import { c as _c } from "react/compiler-runtime";
import { Dialog, Stack, type DialogProps } from '@mui/material';
import classnames from 'classnames';
import React, { type PropsWithChildren } from 'react';
import { Loader } from '@/components/shared/loader';
import { PanelWrapper, type PanelWrapperProps } from '../panels/panelWrapper';
export interface MainDialogProps extends PanelWrapperProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose?: () => void;
  disableGutters?: boolean;
  /** Render as a full-viewport overlay instead of a centered modal. */
  fullScreen?: boolean;
  /**
   * Pass 0 for dialogs that replace one another (e.g. wizard-like step screens): each screen is its
   * own Dialog, so the default fade replays from transparent on every swap and reads as a flash.
   * An `{ enter, exit }` object animates the two directions independently.
   */
  transitionDuration?: DialogProps['transitionDuration'];
  /**
   * Fires after the exit transition finishes. Unmounting on close skips the exit fade entirely, so a
   * caller that wants one must flip `isOpen` to false and unmount from this callback instead.
   */
  onExited?: () => void;
  /**
   * Makes the panel body the scroll region when content exceeds the dialog's height cap: the header
   * and footer stay pinned and the scrollbar is clipped inside the corner radius. Off by default —
   * without it, overflowing content scrolls the whole paper, header and footer included.
   */
  scrollableBody?: boolean;
}
export function MainDialog(props) {
  const $ = _c(39);
  const {
    isLoading,
    isOpen,
    header,
    footer,
    sx,
    disableGutters: t0,
    fullScreen: t1,
    transitionDuration,
    onExited,
    onClose,
    scrollableBody: t2
  } = props;
  const disableGutters = t0 === undefined ? false : t0;
  const fullScreen = t1 === undefined ? false : t1;
  const scrollableBody = t2 === undefined ? false : t2;
  const t3 = onClose ?? header?.onClose;
  let t4;
  if ($[0] !== onExited) {
    t4 = {
      onExited
    };
    $[0] = onExited;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== fullScreen || $[3] !== scrollableBody || $[4] !== sx) {
    t5 = {
      sx: fullScreen ? {
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        borderRadius: "0px",
        ...sx
      } : {
        width: "40%",
        maxWidth: "840px",
        minWidth: "600px",
        height: "max-content",
        minHeight: "300px",
        maxHeight: "90vh",
        borderRadius: "16px",
        ...(scrollableBody && {
          overflow: "hidden"
        }),
        ...sx
      }
    };
    $[2] = fullScreen;
    $[3] = scrollableBody;
    $[4] = sx;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = {
      transition: t4,
      paper: t5
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== header || $[10] !== onClose) {
    t7 = header ? {
      ...header,
      onClose: header.onClose ?? onClose
    } : undefined;
    $[9] = header;
    $[10] = onClose;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== footer) {
    t8 = footer ? {
      ...footer
    } : undefined;
    $[12] = footer;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== scrollableBody) {
    t9 = scrollableBody ? {
      minHeight: 0
    } : undefined;
    $[14] = scrollableBody;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !disableGutters;
  let t11;
  if ($[16] !== t10) {
    t11 = classnames("h-full", {
      "py-[24px]": t10
    });
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== isLoading) {
    t12 = isLoading && <Loader />;
    $[18] = isLoading;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isLoading || $[21] !== props.children) {
    t13 = !isLoading && <>{props.children}</>;
    $[20] = isLoading;
    $[21] = props.children;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t13) {
    t14 = <Stack className={t11}>{t12}{t13}</Stack>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t15 = <PanelWrapper rounded={true} header={t7} footer={t8} sx={t9}>{t14}</PanelWrapper>;
    $[27] = t14;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== fullScreen || $[33] !== isOpen || $[34] !== t15 || $[35] !== t3 || $[36] !== t6 || $[37] !== transitionDuration) {
    t16 = <Dialog fullScreen={fullScreen} transitionDuration={transitionDuration} onClose={t3} onClick={_temp} open={isOpen} slotProps={t6}>{t15}</Dialog>;
    $[32] = fullScreen;
    $[33] = isOpen;
    $[34] = t15;
    $[35] = t3;
    $[36] = t6;
    $[37] = transitionDuration;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
}
function _temp(event) {
  return event.stopPropagation();
}